import React, { useState } from 'react';
import CountrySelect from '@/components/CountrySelect';
import { Address } from '@/types';

interface AddressFormState {
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
}

interface AddressFieldProps {
  label: string;
  value: Address | null;
  onSave: (address: Address) => void;
}

const EMPTY_STATE: AddressFormState = {
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  zip_code: '',
  country: 'United States',
};

const toFormState = (address: Address | null): AddressFormState => {
  if (!address) return EMPTY_STATE;
  return {
    address_line_1: address.address_line_1,
    address_line_2: address.address_line_2 || '',
    city: address.city,
    state: address.state || '',
    zip_code: address.zip_code || '',
    country: address.country,
  };
};

const formatDisplay = (address: Address | null): string => {
  if (!address) return 'No address entered';
  const parts = [
    address.address_line_1,
    address.address_line_2,
    address.city,
    address.state,
    address.zip_code,
    address.country,
  ].filter(Boolean);
  return parts.join(', ') || 'No address entered';
};

const getStateLabel = (country: string): string => {
  switch (country) {
    case 'United States': return 'State';
    case 'Canada': return 'Province';
    case 'United Kingdom': return 'County';
    case 'Australia': return 'State/Territory';
    default: return 'State/Province';
  }
};

const getPostalCodeLabel = (country: string): string => {
  switch (country) {
    case 'United States': return 'ZIP Code';
    case 'Canada': return 'Postal Code';
    case 'United Kingdom': return 'Postcode';
    default: return 'Postal Code';
  }
};

const AddressField: React.FC<AddressFieldProps> = ({ label, value, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [fields, setFields] = useState<AddressFormState>(toFormState(value));
  const [error, setError] = useState<string | null>(null);

  const handleEdit = () => {
    setFields(toFormState(value));
    setError(null);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (!fields.address_line_1.trim()) {
      setError('Street address is required.');
      return;
    }
    setError(null);
    onSave({
      ...value,
      address_line_1: fields.address_line_1,
      address_line_2: fields.address_line_2 || undefined,
      city: fields.city,
      state: fields.state || undefined,
      zip_code: fields.zip_code || undefined,
      country: fields.country,
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFields(toFormState(value));
    setError(null);
    setIsEditing(false);
  };

  return (
    <div className="profile-form-field">
      <label className="profile-form-label">{label}</label>
      {!isEditing ? (
        <div className="profile-address-display">
          {formatDisplay(value)}
          <button className="profile-address-edit-btn" onClick={handleEdit}>Edit</button>
        </div>
      ) : (
        <div className="profile-address-edit-form">
          <div className={`profile-form-field ${error ? 'profile-form-field-error' : ''}`}>
            <label className="profile-form-label">Street Address</label>
            <input
              type="text"
              className="profile-form-input"
              placeholder="123 Main Street"
              value={fields.address_line_1}
              onChange={(e) => {
                setFields(prev => ({ ...prev, address_line_1: e.target.value }));
                if (error) setError(null);
              }}
            />
            {error && <p className="profile-form-error-text">{error}</p>}
          </div>
          <div className="profile-form-field">
            <label className="profile-form-label">Apt, Suite, Unit (optional)</label>
            <input
              type="text"
              className="profile-form-input"
              placeholder="Apt 4B"
              value={fields.address_line_2}
              onChange={(e) => setFields(prev => ({ ...prev, address_line_2: e.target.value }))}
            />
          </div>
          <div className="profile-form-row">
            <div className="profile-form-field">
              <label className="profile-form-label">City</label>
              <input
                type="text"
                className="profile-form-input"
                placeholder="City"
                value={fields.city}
                onChange={(e) => setFields(prev => ({ ...prev, city: e.target.value }))}
              />
            </div>
            <div className="profile-form-field">
              <label className="profile-form-label">{getStateLabel(fields.country)}</label>
              <input
                type="text"
                className="profile-form-input"
                placeholder={fields.country === 'United States' ? 'CA' : 'State/Province'}
                value={fields.state}
                onChange={(e) => setFields(prev => ({ ...prev, state: e.target.value }))}
              />
            </div>
          </div>
          <div className="profile-form-row">
            <div className="profile-form-field">
              <label className="profile-form-label">{getPostalCodeLabel(fields.country)}</label>
              <input
                type="text"
                className="profile-form-input"
                placeholder={fields.country === 'United States' ? '90210' : 'Postal Code'}
                value={fields.zip_code}
                onChange={(e) => setFields(prev => ({ ...prev, zip_code: e.target.value }))}
              />
            </div>
            <div className="profile-form-field">
              <label className="profile-form-label">Country</label>
              <CountrySelect
                value={fields.country}
                onChange={(val) => setFields(prev => ({ ...prev, country: val }))}
              />
            </div>
          </div>
          <div className="profile-address-actions">
            <button className="profile-address-save-btn" onClick={handleSave}>Save</button>
            <button className="profile-address-cancel-btn" onClick={handleCancel}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressField;
